import { Opt, TransientPick } from 'app/tools/general.types';
import _ from 'lodash';
import React, { useContext, useMemo } from 'react';
import { useResizeDetector } from 'react-resize-detector/build/withPolyfill';
import styled from 'styled-components/macro';

export type ViewportHook = {
  vHeight: number;
  vWidth: number;
  vh: (n: number) => number;
  vw: (n: number) => number;
};
const ViewportContext =
  React.createContext<Opt<ViewportHook>>(undefined);

type Props = {
  onlyUpdateWidth?: boolean;
  onlyUpdateHeight?: boolean;
  rootProvider?: boolean;
  children: React.ReactNode;
};

type FullscreenDivOpts = TransientPick<
  Props,
  'rootProvider' | 'onlyUpdateHeight'
>;
const FullscreenDiv = styled.div<FullscreenDivOpts>`
  flex-direction: column;
  width: 100%;
  height: ${p => (p.$rootProvider ? '100%' : 'auto')};
  display: flex;
  flex: ${p => (p.$rootProvider ? 0 : 1)} 1 100%;
  overflow-y: ${p => (p.$onlyUpdateHeight ? 'hidden' : 'initial')};
`;

const defaultW = 375;
const defaultH = 680;

/**
 * Provides nearest measured width and height as 'vWidth' and
 * 'vHeight' to any 'useViewport()' hook calls.
 */
export function ViewportProvider({
  onlyUpdateWidth = false,
  onlyUpdateHeight = false,
  rootProvider = false,
  children,
}: Props): JSX.Element {
  const existingVP = useContext(ViewportContext);

  const existingW = existingVP?.vWidth || defaultW;
  const existingH = existingVP?.vHeight || defaultH;

  const { width: newW, height: newH, ref } = useResizeDetector();

  const vWidth = (!onlyUpdateHeight && newW) || existingW;
  const vHeight = (!onlyUpdateWidth && newH) || existingH;

  const viewportValue: ViewportHook = useMemo(
    () => ({
      vHeight: _.floor(vHeight, 1),
      vWidth: _.floor(vWidth, 1),
      vh: n => (n * vHeight) / 100,
      vw: n => (n * vWidth) / 100,
    }),
    [vHeight, vWidth]
  );

  return (
    <ViewportContext.Provider value={viewportValue}>
      <FullscreenDiv
        $rootProvider={rootProvider}
        $onlyUpdateHeight={onlyUpdateHeight}
        ref={ref}
      >
        {children}
      </FullscreenDiv>
    </ViewportContext.Provider>
  );
}

export const useViewport = (): ViewportHook => {
  const viewport = useContext(ViewportContext);
  if (!viewport) {
    throw new Error('Cannot access viewport outside of provider.');
  }
  return viewport;
};
